import { useCallback, useMemo, useState } from 'react';

/**
 * Estado de um editor de conteúdo no padrão "novo vs. editando".
 *
 * - `selectedId === ''` → modo novo (Salvar cria).
 * - `selectedId !== ''` → modo edição (Salvar/Editar regravam a chave).
 * - `dirty` compara o formulário com o último estado carregado/salvo.
 */
export function useContentEditor<T extends object>(emptyValues: T) {
  const [values, setValues] = useState<T>(emptyValues);
  const [baseline, setBaseline] = useState<T>(emptyValues);
  const [selectedId, setSelectedId] = useState('');

  const editing = selectedId !== '';

  const dirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(baseline),
    [values, baseline],
  );

  /** Atualiza um campo do formulário. */
  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((previous) => ({ ...previous, [key]: value }));
  }, []);

  /** Carrega um item existente no formulário (modo edição). */
  const select = useCallback((id: string, itemValues: T) => {
    setSelectedId(id);
    setValues(itemValues);
    setBaseline(itemValues);
  }, []);

  /** Volta ao modo novo, com os campos vazios. */
  const clear = useCallback(() => {
    setSelectedId('');
    setValues(emptyValues);
    setBaseline(emptyValues);
  }, [emptyValues]);

  /** Marca o estado atual como salvo (após um POST bem-sucedido). */
  const markSaved = useCallback((nextValues?: T) => {
    const snapshot = nextValues ?? values;
    setValues(snapshot);
    setBaseline(snapshot);
  }, [values]);

  return {
    values,
    setValues,
    setField,
    selectedId,
    editing,
    dirty,
    select,
    clear,
    markSaved,
  };
}
